import { motion } from "framer-motion";
import { Boxes, BrainCircuit, Megaphone, Server, Users } from "lucide-react";
import { EASE, Overline, Reveal } from "./Reveal";

const STEPS = [
  {
    icon: Users,
    verb: "Hire",
    entity: "Talent Connect",
    line: "Verified specialists in seat — first qualified submissions in 36 hours.",
  },
  {
    icon: Server,
    verb: "Build",
    entity: "Dignifyd Tech",
    line: "Cloud, applications and managed IT for the enterprise estate.",
  },
  {
    icon: Boxes,
    verb: "Run",
    entity: "Enterprise Solutions",
    line: "ERP and infrastructure that stay stable, secure and maintainable.",
  },
  {
    icon: Megaphone,
    verb: "Grow",
    entity: "Dignifyd Digital",
    line: "Brand, demand generation and creative that take you to market.",
  },
  {
    icon: BrainCircuit,
    verb: "Decide",
    entity: "Dignifyd Data Labs",
    line: "Data engineering and applied AI for intelligent decision systems.",
  },
];

const ICON_MOTION = [
  { animate: { scale: [1, 1.18, 1] }, transition: { duration: 2.2, repeat: Infinity, ease: "easeInOut" } },
  { animate: { y: [0, -3, 0] }, transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" } },
  { animate: { rotate: [0, 8, -8, 0] }, transition: { duration: 3.4, repeat: Infinity, ease: "easeInOut" } },
  { animate: { rotate: [0, -12, 0], x: [0, 2, 0] }, transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } },
  { animate: { scale: [1, 1.22, 1], opacity: [1, 0.7, 1] }, transition: { duration: 1.6, repeat: Infinity, ease: "easeInOut" } },
];

export default function WhatWeDo() {
  return (
    <section
      id="what-we-do"
      data-testid="what-we-do-section"
      className="relative scroll-mt-24 border-b border-white/10"
    >
      <div className="absolute inset-0 bg-grid opacity-40" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <Reveal>
          <Overline>What we do — in one line</Overline>
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-2xl font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              We build, hire, run, grow and{" "}
              <span className="text-gold">decide</span> — as one partner.
            </h2>
            <p className="max-w-md text-base leading-relaxed text-gray-400">
              Most enterprises juggle five vendors for this. Dignifyd does it through
              five specialist companies that share one contract, one governance
              standard and one delivery backbone — so nothing gets lost between
              suppliers.
            </p>
          </div>
        </Reveal>

        <div className="relative mt-16">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.8, ease: EASE }}
            className="absolute left-0 right-0 top-6 hidden h-px origin-left bg-white/15 lg:block"
            aria-hidden
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.verb}
                data-testid={`what-step-${i}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
                className="group relative border border-white/10 bg-ink p-6 transition-colors duration-500 hover:border-gold/50"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center border border-white/15 bg-surface text-gold transition-colors duration-500 group-hover:border-gold/40">
                    <motion.span
                      data-testid={`what-step-icon-${i}`}
                      animate={ICON_MOTION[i].animate}
                      transition={ICON_MOTION[i].transition}
                      className="inline-flex"
                    >
                      <s.icon className="h-5 w-5" />
                    </motion.span>
                  </span>
                  <span className="font-mono text-xs tracking-[0.25em] text-gray-600">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white">
                  {s.verb}
                </h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  {s.entity}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{s.line}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="speed-gap-infographic"
            className="mt-16 border border-white/10 bg-surface/60 p-6 sm:p-8"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
              The speed gap — first qualified talent submission
            </p>
            <div className="mt-6 flex flex-col gap-6">
              <div>
                <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
                  <span className="text-white">Dignifyd</span>
                  <span className="text-gold">36 hours</span>
                </div>
                <div className="mt-2 h-2 w-full bg-white/10">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "3%" }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 1.2, ease: EASE }}
                    className="h-2 bg-gold"
                  />
                </div>
              </div>
              <div>
                <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
                  <span className="text-gray-400">Industry average — niche roles</span>
                  <span className="text-gray-400">60+ days</span>
                </div>
                <div className="mt-2 h-2 w-full bg-white/10">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 1.8, ease: EASE }}
                    className="h-2 bg-white/30"
                  />
                </div>
              </div>
            </div>
            <p className="mt-6 text-xs leading-relaxed text-gray-500">
              Average time-to-hire for niche roles via traditional channels vs Dignifyd
              Talent Connect's first-submission turnaround. Source: Dignifyd Talent
              Connect company profile.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
